import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { DateRange, MetricRange } from "./experiment.entity";

/**
 * A resolved date range as a concrete pair of Unix epoch-millisecond timestamps — the exact wire
 * shape `GetExperimentResultsRequest.from`/`to` expects. Both bounds are inclusive.
 */
export interface ResolvedRange {
  from: number; // epoch ms, inclusive
  to: number; // epoch ms, inclusive
}

/**
 * The narrow slice of `ExperimentEntity` fields `resolveMetricRange` needs for the `FULL` range.
 * Deliberately narrow (three fields) rather than the full entity — this keeps the resolver
 * trivially testable with a two-line fixture instead of a full experiment object.
 */
export interface ExperimentRangeInput {
  /** The experiment's start instant (always present per US-001's `ExperimentSchema.startedAt`) —
   *  the backend's sole source of truth for both the planned/actual start date and DRAFT/RUNNING
   *  resolution. */
  startedAt: Date;
  /** The instant the experiment was archived, if known. Always `null` today — no wire field
   *  currently carries this value (see proposal.md, "Plan vs. Actual Implementation"); kept as an
   *  explicit input so a future backend-derived value can be wired in by the caller with no change
   *  to this function's signature. */
  archivedAt: Date | null;
}

/**
 * Thrown when `hotelTimeZone` is not a valid, recognized IANA time zone identifier. Callers must
 * not swallow this into a default-zone computation — surfacing a clear error is preferable to
 * silently computing a wrong hotel's calendar days.
 */
export class InvalidHotelTimeZoneError extends Error {
  constructor(public readonly timeZone: string, cause: unknown) {
    super(`Invalid IANA time zone "${timeZone}" passed to resolveMetricRange`);
    this.name = "InvalidHotelTimeZoneError";
    this.cause = cause;
  }
}

interface CalendarDate {
  year: number;
  month: number; // 0-indexed, matches `Date#getMonth()`/the local `Date` constructor
  day: number;
}

/**
 * Verifies `hotelTimeZone` is a syntactically valid, recognized IANA identifier, throwing
 * `InvalidHotelTimeZoneError` otherwise. This explicit check is required because the installed
 * `date-fns-tz` version's `utcToZonedTime`/`zonedTimeToUtc` do NOT throw for an invalid zone —
 * they silently produce an `Invalid Date` (verified against the installed library source), which
 * would otherwise propagate as silent `NaN` timestamps instead of a clear, catchable error.
 */
function assertValidHotelTimeZone(hotelTimeZone: string): void {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: hotelTimeZone });
  } catch (cause) {
    throw new InvalidHotelTimeZoneError(hotelTimeZone, cause);
  }
}

/**
 * What calendar date is it right now, in `hotelTimeZone`?
 *
 * Implementation note: the installed `date-fns-tz` (`utcToZonedTime`/`zonedTimeToUtc`) round-trips
 * calendar values through the JS `Date` object's *local* (ambient-time-zone) getters/setters
 * internally — verified by reading its source (`utcToZonedTime` builds its result via
 * `setFullYear`/`setHours`, not `setUTCFullYear`/`setUTCHours`). Consequently, its result MUST be
 * read back with local (non-UTC) getters — reading with UTC getters would incorrectly depend on
 * the *process's* ambient time zone (the exact browser-TZ leakage this module exists to prevent).
 * Local getters paired with this library's local-setter-based output are ambient-TZ-independent by
 * construction (verified empirically across multiple `TZ` values) — this is the correct pattern
 * for this library, not a local-getter leak.
 */
function hotelLocalCalendarDate(instant: Date, hotelTimeZone: string): CalendarDate {
  assertValidHotelTimeZone(hotelTimeZone);
  const zoned = utcToZonedTime(instant, hotelTimeZone);
  return {
    year: zoned.getFullYear(),
    month: zoned.getMonth(),
    day: zoned.getDate(),
  };
}

/**
 * The real UTC instant corresponding to local midnight of `localDate` in `hotelTimeZone`.
 *
 * Implementation note: mirroring `hotelLocalCalendarDate` above, `zonedTimeToUtc` reads its input
 * `Date` via *local* getters internally (verified by reading its source). The wall-clock values
 * this function intends to convert must therefore be carried via the local `Date` constructor
 * (`new Date(year, month, day, ...)`), never `Date.UTC(...)` — using `Date.UTC` here would make the
 * result depend on the process's ambient time zone instead of `hotelTimeZone`. The local
 * constructor natively normalizes an out-of-range `day` (e.g. `day: -3`), so no manual month/year
 * rollover arithmetic is needed by callers.
 */
function startOfHotelDay(localDate: CalendarDate, hotelTimeZone: string): Date {
  assertValidHotelTimeZone(hotelTimeZone);
  const wallClockMidnight = new Date(localDate.year, localDate.month, localDate.day, 0, 0, 0, 0);
  return zonedTimeToUtc(wallClockMidnight, hotelTimeZone);
}

/** Reads a `Date`'s intended calendar date via UTC-getters only — see `resolveCustom`'s contract. */
function readUtcDateParts(date: Date): CalendarDate {
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth(),
    day: date.getUTCDate(),
  };
}

/**
 * The last N fully-completed calendar days in `hotelTimeZone`, excluding today (today's data is
 * known-incomplete). E.g. queried on Sept 1, `resolveLastNDays(7, ...)` resolves to Aug 25-31.
 */
function resolveLastNDays(n: number, hotelTimeZone: string, now: Date): ResolvedRange {
  const today = hotelLocalCalendarDate(now, hotelTimeZone);
  const from = startOfHotelDay({ ...today, day: today.day - n }, hotelTimeZone).getTime();
  const to = startOfHotelDay(today, hotelTimeZone).getTime() - 1;
  return { from, to };
}

/**
 * `[startedAt, min(now, archivedAt ?? now)]` — a direct translation of the historical BM
 * semantics. Boundaries are NOT snapped to calendar-day midnight: `startedAt` is already a
 * precise backend instant, not a user-picked calendar date.
 */
function resolveFull(experiment: ExperimentRangeInput, now: Date): ResolvedRange {
  const from = experiment.startedAt.getTime();
  const to = Math.min(now.getTime(), (experiment.archivedAt ?? now).getTime());
  return { from, to };
}

/**
 * The picked `custom.from`/`custom.to` dates, interpreted as full inclusive calendar days in
 * `hotelTimeZone`. Contract: `custom.from`/`custom.to` MUST be constructed via
 * `new Date(Date.UTC(year, month, day))` (or equivalent UTC-midnight construction) for a picked
 * calendar date — never `new Date(year, month, day)`, which encodes the date using the browser's
 * local time zone and would reintroduce the exact browser-TZ leakage this resolver exists to
 * prevent.
 */
function resolveCustom(custom: DateRange, hotelTimeZone: string): ResolvedRange {
  const fromParts = readUtcDateParts(custom.from);
  const toParts = readUtcDateParts(custom.to);
  const from = startOfHotelDay(fromParts, hotelTimeZone).getTime();
  const to = startOfHotelDay({ ...toParts, day: toParts.day + 1 }, hotelTimeZone).getTime() - 1;

  if (from > to) {
    throw new Error("resolveMetricRange: custom.from must not be after custom.to");
  }

  return { from, to };
}

/**
 * Translates a `MetricRange` selection into a concrete `{ from, to }` pair of Unix epoch-ms
 * timestamps — the single place where "what does a day mean for this hotel" is decided.
 *
 * Pure and synchronous: performs no network call, never reads `usePropertyConfiguration()` /
 * `useHotelTimeZone()` itself, and never reads `experiment.entity.ts`'s proto-facing epoch-ms
 * fields directly. The caller is responsible for obtaining `hotelTimeZone` (via
 * `useHotelTimeZone()`) and the relevant `Date` fields off an already-fetched experiment.
 *
 * @param range The selected metric range.
 * @param hotelTimeZone The hotel's IANA time zone (e.g. "Europe/Madrid"). Always the explicit
 *   source of truth for calendar-day boundaries — never the browser's local zone, never UTC.
 * @param experiment The narrow experiment fields needed for the `FULL` range.
 * @param custom The user-picked date range, required only when `range === "CUSTOM"`.
 * @param now Overridable clock, defaulting to `new Date()`. Injectable for deterministic tests.
 */
export function resolveMetricRange(
  range: MetricRange,
  hotelTimeZone: string,
  experiment: ExperimentRangeInput,
  custom?: DateRange,
  now: Date = new Date()
): ResolvedRange {
  switch (range) {
    case "LAST_7":
      return resolveLastNDays(7, hotelTimeZone, now);
    case "LAST_15":
      return resolveLastNDays(15, hotelTimeZone, now);
    case "LAST_30":
      return resolveLastNDays(30, hotelTimeZone, now);
    case "FULL":
      return resolveFull(experiment, now);
    case "CUSTOM":
      if (!custom) {
        throw new Error("resolveMetricRange: 'custom' is required when range is 'CUSTOM'");
      }
      return resolveCustom(custom, hotelTimeZone);
    default: {
      const exhaustiveCheck: never = range;
      throw new Error(`resolveMetricRange: unsupported range "${exhaustiveCheck}"`);
    }
  }
}
